<template>
  <div class="asc-open-maintenance-grid">
    <asc-ag-grid-filter
      :key="`asc-open-maintenance-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscDrillDownCellRenderer,
  AscDrillDownReadCellRenderer,
  AscMxStatusIconCellRenderer,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'
import { getMaintenanceStatusValue } from '@/utilities/maintenance'
import { MAINTENANCE_GRID_SORT_COLUMNS } from '@/utilities/appConsts'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscOpenMaintenanceGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscDrillDownCellRenderer,
    AscDrillDownReadCellRenderer,
    AscMxStatusIconCellRenderer,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  data() {
    return {
      localStorageName: 'maintenance_grid_read_ids',
      updateDateFieldName: 'lastAlertDate',
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        pagination: false,
        getContextMenuItems: this.getContextMenuItems,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
      statusColumnSortOrder: MAINTENANCE_GRID_SORT_COLUMNS,
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(item => {
        return {
          ...item,
          analyticModule:
            item.analyticModule === null ? 'N/A' : item.analyticModule,
          systemName:
            item.sourceName === 'Custom Alert' ||
            item.sourceName === 'Repeater Event'
              ? '-'
              : item.systemName === null
              ? 'N/A'
              : item.systemName,
          componentName:
            item.componentName === null ? 'N/A' : item.componentName,
        }
      })
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)

      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(this.gridColumns, 'issueId', 'ID'),
          field: 'issueId',
          cellClass: this.isColumnDrillSupported(this.gridColumns, 'issueId')
            ? ['ag-cell--drill', 'ag-cell--drill-ripple']
            : null,
          cellRenderer: this.isColumnDrillSupported(this.gridColumns, 'issueId')
            ? 'AscDrillDownCellRenderer'
            : null,
          cellRendererParams: {
            localStorageName: this.localStorageName,
            updateDateFieldName: this.updateDateFieldName,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'issueId'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'statusName',
            'Status',
          ),
          field: 'statusName',
          cellRenderer: 'AscMxStatusIconCellRenderer',
          width: 300,
          minWidth: 300,
          comparator: function (status1, status2) {
            const status1Value = getMaintenanceStatusValue(status1)
            const status2Value = getMaintenanceStatusValue(status2)
            return status2Value - status1Value
          },
          hide: !this.isColumnVisible(this.gridColumns, 'statusName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'systemName',
            'System',
          ),
          field: 'systemName',
          hide: !this.isColumnVisible(this.gridColumns, 'systemName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'sourceName',
            'Issue Source',
          ),
          field: 'sourceName',
          hide: !this.isColumnVisible(this.gridColumns, 'sourceName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'alertCount',
            'Alert Count',
          ),
          field: 'alertCount',
          filter: 'agNumberColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'alertCount'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'lastAlertDate',
            'Date Last Alert',
          ),
          field: 'lastAlertDate',
          width: 165,
          minWidth: 165,
          sort: 'desc',
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
          hide: !this.isColumnVisible(this.gridColumns, 'lastAlertDate'),
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    handleCellClicked(event) {
      if (event) {
        const field = event.colDef.field
        switch (field) {
          case 'issueId': {
            if (this.isColumnDrillSupported(this.gridColumns, 'issueId')) {
              this.$router.push({
                name: 'Mx Recs',
                params: {
                  issueId: event.value,
                },
              })

              this.updateLocalStorage(
                this.localStorageName,
                this.updateDateFieldName,
                event,
              )
            }
            break
          }
          default:
            break
        }
      }
    },

    getContextMenuItems(params) {
      const _that = this
      const issueId = params.node.data.issueId
      const lastAlertDate = params.node.data.lastAlertDate
      const event = {
        value: issueId,
        data: {
          lastAlertDate,
        },
      }
      const url = `maintenance/issueid/${issueId}`
      const result = [
        {
          name: 'Open Link in New Tab',
          action: function () {
            _that.updateLocalStorage(
              _that.localStorageName,
              _that.updateDateFieldName,
              event,
            )
            window.open(url, '_blank', 'noopener', 'noreferrer')
          },
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]

      return result
    },
  },
}
</script>

<style lang="scss">
.asc-open-maintenance-grid {
  .ag-theme-balham {
    height: 360px !important;
  }

  .asc-sort-icon {
    color: $primary_black;
    position: absolute;
    top: 14px;
    right: 16px;
  }

  .asc-filter-icon {
    color: $primary_black;
  }

  .asc-grid-wrapper-header--container {
    .asc-grid-wrapper-header-buttons--container {
      .row {
        .col:first-child {
          margin-right: 10px;
        }
      }
    }
  }
}
</style>
